import React, { useCallback, useState } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { DialogTitle } from 'shadcn/components/ui/Dialog';
import { useAllocatedLabelPrinting } from 'shared/hooks/receiving/useAllocatedLabelPrinting';

import { PrintAllocatedLabelsSummaryCard } from './PrintAllocatedLabelsSummaryCard';
import {
    DEFAULT_TAGS_PER_PALLET,
    getLineItemDisplayFields,
    isValidTagsPerPallet,
    sanitizeTagsPerPalletInput,
} from './printLabelsUtils';
import { PrintPalletCaseTags } from './PrintPalletCaseTags';
import { PrintPalletTagsCard } from './PrintPalletTagsCard';

/**
 * @param {Object} props
 * @param {boolean} [props.isThirdPartyAsn]
 * @param {string} props.poType
 * @param {string | null} [props.partnerDisplayName]
 * @param {object} props.receivedPrintData
 * @param {object} props.rowData
 */
function PrintAllocatedLabelsModal({
    isThirdPartyAsn = false,
    partnerDisplayName = null,
    poType,
    receivedPrintData,
    receiveWarnings = [],
    rowData,
}) {
    const { itemName, sku, unitsPerCase } = getLineItemDisplayFields(rowData, poType);
    const [tagsPerPallet, setTagsPerPallet] = useState(DEFAULT_TAGS_PER_PALLET);
    const [caseLabelQtyByLot, setCaseLabelQtyByLot] = useState(() => new Map());
    const numPallets = receivedPrintData.pallets.length;

    const handlePrintError = useCallback(() => {
        toast.error('Failed to print labels');
    }, []);

    const handleDownloadError = useCallback(() => {
        toast.error('Failed to download labels');
    }, []);

    const { downloadAllLabels, isPrinting, printAllPalletTags, printCaseTags, printPalletTags } =
        useAllocatedLabelPrinting({
            onDownloadError: handleDownloadError,
            onError: handlePrintError,
            receivedPrintData,
        });

    const handleTagsPerPalletChange = useCallback((event) => {
        setTagsPerPallet(sanitizeTagsPerPalletInput(event.target.value));
    }, []);

    const handleCaseLabelCountChange = useCallback((lotId, qty) => {
        setCaseLabelQtyByLot((previous) => {
            const next = new Map(previous);
            next.set(lotId, qty);
            return next;
        });
    }, []);

    const getCaseLabelCount = useCallback(
        (lot) => {
            const stored = caseLabelQtyByLot.get(lot.id);
            return stored !== undefined ? stored : lot.caseCount;
        },
        [caseLabelQtyByLot],
    );

    const handleDownloadAllLabels = useCallback(() => {
        if (!isValidTagsPerPallet(tagsPerPallet)) {
            return;
        }

        downloadAllLabels({ caseLabelQtyByLot, tagsPerPallet });
    }, [caseLabelQtyByLot, downloadAllLabels, tagsPerPallet]);

    const handlePrintPalletTags = useCallback(
        (palletId) => {
            if (!isValidTagsPerPallet(tagsPerPallet)) {
                return;
            }

            printPalletTags({ palletId, qty: tagsPerPallet });
        },
        [printPalletTags, tagsPerPallet],
    );

    return (
        <>
            <div className="flex items-center justify-between gap-4 border-b py-4 pl-6 pr-12">
                <DialogTitle className="p-0 text-base font-semibold">Print Labels</DialogTitle>
                <Button
                    className="bg-[#4C884B] text-white hover:bg-[#4C884B]/90"
                    disabled={isPrinting || !isValidTagsPerPallet(tagsPerPallet)}
                    type="button"
                    onClick={handleDownloadAllLabels}
                >
                    <Download aria-hidden className="size-4" />
                    Download All Labels
                </Button>
            </div>
            <div className="p-6">
                {receiveWarnings.length > 0 ? (
                    <Alert className="mb-4 border-amber-500 bg-amber-50 text-amber-900">
                        <AlertTitle>Received with warnings</AlertTitle>
                        <AlertDescription>
                            <ul className="list-disc pl-4">
                                {receiveWarnings.map((warning) => (
                                    <li key={warning}>{warning}</li>
                                ))}
                            </ul>
                        </AlertDescription>
                    </Alert>
                ) : null}
                <PrintAllocatedLabelsSummaryCard
                    itemName={itemName}
                    numPallets={numPallets}
                    sku={sku}
                    totalCases={receivedPrintData.totalCases}
                    unitsPerCase={unitsPerCase}
                />
                <PrintPalletTagsCard
                    className="mt-4"
                    isPrinting={isPrinting}
                    numPallets={numPallets}
                    tagsPerPallet={tagsPerPallet}
                    onPrintAllPalletTags={(qty) => printAllPalletTags({ tagsPerPallet: qty })}
                    onTagsPerPalletChange={handleTagsPerPalletChange}
                />
                <div className="mt-6 flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                        Per-Pallet Breakdown
                    </span>
                    <div className="h-px flex-1 bg-border" />
                </div>
                <div className="mt-3 flex max-h-[min(50vh,28rem)] flex-col gap-2 overflow-y-auto">
                    {receivedPrintData.pallets.map((pallet, index) => (
                        <PrintPalletCaseTags
                            key={pallet.id}
                            getCaseLabelCount={getCaseLabelCount}
                            isThirdPartyAsn={isThirdPartyAsn}
                            isPrinting={isPrinting}
                            pallet={pallet}
                            palletIndex={index}
                            partnerDisplayName={partnerDisplayName}
                            tagsPerPallet={tagsPerPallet}
                            onCaseLabelCountChange={handleCaseLabelCountChange}
                            onPrintCaseTags={(lotId, qty) => printCaseTags({ lotId, qty })}
                            onPrintPalletTags={handlePrintPalletTags}
                        />
                    ))}
                </div>
            </div>
        </>
    );
}

export { PrintAllocatedLabelsModal };
